import { useState } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import tw from 'twin.macro';

import { SIMILAR_DATE_CARS } from '../../../services/carService/queries';
import { CarCard } from '../../../components/car/searchResult/carCard';
import { similarDatesFlexDays, similarDatesNote } from '../../../helpers/monthlyTrip';
import { Colors } from '../../../styles/colors';

/**
 * Spec 028 "similar dates" (Turo's "Show cars with similar dates"): below the results, a
 * guest can ask for cars that are not free for these exact dates but are for the same trip
 * moved a few days (up to 7 for a trip of 30 days or more, else 3). Each card shows its
 * moved dates and is priced for them; opening it carries those dates to the car page.
 * The section is its own query, so an API without `similarDateCars` only hides it.
 */
export type SimilarDatesSearchInput = {
  locationId?: number;
  latitude?: number;
  longitude?: number;
  radiusMiles?: number;
  startAt: number;
  endAt: number;
  driverAge: number;
  type?: string;
  filters?: unknown;
  siteHost?: string;
};

type SimilarDateCar = {
  id: string;
  make?: string | null;
  weekdayPrice?: number | null;
  isNew?: boolean | null;
  distanceMiles?: number | null;
  images?: Array<{ imageUrl?: string | null }> | null;
  location?: { city?: string | null } | null;
  calculatedPrice?: any;
  shiftDays: number;
};

const Section = styled.section`
  ${tw`w-full flex flex-col`}
  gap: 12px;
  margin-top: 16px;
`;

const ToggleRow = styled.label`
  ${tw`flex items-start cursor-pointer`}
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-bg-elevated, #fff);

  input {
    margin-top: 3px;
    width: 16px;
    height: 16px;
    accent-color: var(--color-black);
  }
`;

const ToggleText = styled.span`
  ${tw`flex flex-col text-sm`}
  color: ${Colors.TextPrimary};
  font-weight: 600;

  small {
    font-weight: 400;
    font-size: 13px;
    color: ${Colors.TextSecondary};
  }
`;

const Heading = styled.h3`
  ${tw`text-base font-semibold m-0`}
  color: ${Colors.TextPrimary};
`;

const Note = styled.p`
  ${tw`text-sm m-0`}
  color: ${Colors.TextSecondary};
`;

const Cards = styled.div`
  ${tw`flex flex-col`}
  gap: 12px;
`;

export function SimilarDatesSection({
  input,
  totalDays,
  startDate,
  returnDate,
  defaultOn,
  urlParamsFor,
}: {
  /** The search's own input; null while it is not known. */
  input: SimilarDatesSearchInput | null;
  totalDays: number;
  /** The searched dates as the URL carries them (ISO). */
  startDate: string;
  returnDate: string;
  /** On when the exact search found nothing. */
  defaultOn: boolean;
  /** The car page params for this search moved by `shiftDays`. */
  urlParamsFor: (shiftDays: number) => string;
}) {
  const [picked, setPicked] = useState<boolean | null>(null);
  const on = picked ?? defaultOn;
  const flexDays = similarDatesFlexDays(totalDays);

  const { data, loading, error } = useQuery<{ similarDateCars: SimilarDateCar[] }>(
    SIMILAR_DATE_CARS,
    {
      skip: !on || !input,
      fetchPolicy: 'cache-first',
      variables: { input: { ...input, flexDays } },
    },
  );

  // An older API without the query: say nothing.
  if (error && !data) return null;

  const cars = data?.similarDateCars ?? [];

  return (
    <Section data-testid="similar-dates-section">
      <ToggleRow>
        <input
          type="checkbox"
          checked={on}
          onChange={e => setPicked(e.target.checked)}
          data-testid="similar-dates-toggle"
        />
        <ToggleText>
          Show cars with similar dates
          <small>
            Same trip length, pickup up to {flexDays} days earlier or later. Prices are for those
            dates.
          </small>
        </ToggleText>
      </ToggleRow>
      {on && loading && <Note>Looking for cars on nearby dates…</Note>}
      {on && !loading && data && cars.length === 0 && (
        <Note data-testid="similar-dates-empty">No cars on nearby dates either.</Note>
      )}
      {on && cars.length > 0 && (
        <>
          <Heading>Available on similar dates ({cars.length})</Heading>
          <Cards>
            {cars.map(car => (
              <CarCard
                key={car.id}
                id={car.id}
                name={car.make ?? ''}
                thumbnailUrl={car.images?.[0]?.imageUrl ?? ''}
                location={car.location?.city ?? ''}
                weekdayPrice={car.weekdayPrice ?? 0}
                isNew={car.isNew ?? false}
                distanceMiles={car.distanceMiles ?? undefined}
                urlParams={urlParamsFor(car.shiftDays)}
                calculatedPrice={car.calculatedPrice}
                datesNote={similarDatesNote(car.shiftDays, startDate, returnDate)}
              />
            ))}
          </Cards>
        </>
      )}
    </Section>
  );
}
